import { useState, useEffect, useRef } from "react";
import { 
  Globe, 
  Server, 
  Shield, 
  DollarSign, 
  ExternalLink, 
   
  Search, 
  ChevronDown, 
  ChevronRight,
  Copy,
  Download,
  Gavel,
  
  
  
  
  Ban,
  Scale,
  
  Clock,
  MapPin,
  Zap,
  CreditCard,
  TrendingUp,
  
  
  
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { toast } from "sonner";
import { infrastructureService, InfrastructureData } from "../src/services/infrastructureService";

interface CaseData {
  id: string;
  title: string;
  type: string;
  urls: string[];
  createdAt: Date;
  status: "active" | "completed" | "pending";
}

interface InfrastructureProps {
  activeCase?: CaseData;
}

// Use the InfrastructureData interface from the service
type UrlAnalysis = InfrastructureData;

// Real data will be fetched from the backend via infrastructureService

const getContactInfo = (service: string) => {
  const contacts: Record<string, any> = {
    "CloudFlare": {
      abuseEmail: "abuse@cloudflare.com",
      legalEmail: "legal@cloudflare.com",
      phone: "+1-650-319-8930",
      address: "101 Townsend St, San Francisco, CA 94107",
      jurisdiction: "United States",
      subpoenaProcess: "Submit via CloudFlare's legal portal with court order",
      takedownProcess: "Submit abuse report with evidence via trust & safety portal",
      litigationHold: "Email legal department with case details and preservation request"
    },
    "Amazon AWS": {
      abuseEmail: "abuse@amazonaws.com",
      legalEmail: "aws-legal@amazon.com",
      phone: "+1-206-266-1000",
      address: "410 Terry Ave N, Seattle, WA 98109",
      jurisdiction: "United States",
      subpoenaProcess: "Submit via AWS Law Enforcement portal with valid legal process",
      takedownProcess: "Report via AWS abuse form with detailed evidence",
      litigationHold: "Contact legal team with formal preservation notice"
    },
    "DigitalOcean": {
      abuseEmail: "abuse@digitalocean.com",
      legalEmail: "legal@digitalocean.com",
      phone: "+1-347-875-6044",
      address: "101 Avenue of the Americas, New York, NY 10013",
      jurisdiction: "United States",
      subpoenaProcess: "Submit legal requests via email with proper documentation",
      takedownProcess: "Email abuse team with evidence and violation details",
      litigationHold: "Send formal preservation notice to legal department"
    },
    "PayPal": {
      abuseEmail: "compliance@paypal.com",
      legalEmail: "subpoena@paypal.com",
      phone: "+1-402-935-7733",
      address: "2211 North First Street, San Jose, CA 95131",
      jurisdiction: "United States",
      subpoenaProcess: "Submit via PayPal's Law Enforcement portal",
      takedownProcess: "Report via PayPal's resolution center",
      litigationHold: "Contact legal department with case number and details"
    },
    "Stripe": {
      abuseEmail: "disputes@stripe.com",
      legalEmail: "legal@stripe.com",
      phone: "+1-888-963-8467",
      address: "510 Townsend Street, San Francisco, CA 94103",
      jurisdiction: "United States",
      subpoenaProcess: "Submit legal requests via Stripe's law enforcement portal",
      takedownProcess: "Report suspicious activity via Stripe dashboard",
      litigationHold: "Email legal team with preservation request"
    }
  };
  
  // Helper function to clean service names for email generation
  const cleanServiceName = (serviceName: string): string => {
    let cleaned = serviceName.toLowerCase().trim();
    
    // Remove common business suffixes
    cleaned = cleaned.replace(/,?\s*(llc|inc|corp|ltd|limited)\.?$/i, '');
    
    // Extract the main part before the first dot or space
    const mainPart = cleaned.split(/[.,\s]/)[0];
    
    // Clean up non-alphanumeric characters
    return mainPart.replace(/[^a-z0-9]/g, '') || 'unknown';
  };

  const cleanService = cleanServiceName(service);

  return contacts[service] || {
    abuseEmail: `abuse@${cleanService}.com`,
    legalEmail: `legal@${cleanService}.com`,
    phone: "Contact via website",
    address: "See company website",
    jurisdiction: "Unknown",
    subpoenaProcess: "Contact legal department for process",
    takedownProcess: "Submit abuse report via website",
    litigationHold: "Email legal department"
  };
};

export function Infrastructure({ activeCase }: InfrastructureProps) {
  const [expandedUrls, setExpandedUrls] = useState<Set<string>>(new Set());
  const [analysisData, setAnalysisData] = useState<UrlAnalysis[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const isLoadingRef = useRef(false);

  // Fetch real infrastructure data from the backend
  useEffect(() => {
    const loadInfrastructureData = async () => {
      if (activeCase) {
        if (isLoadingRef.current) {
          console.log('Skipping infrastructure data load due to ongoing analysis.');
          return;
        }
        isLoadingRef.current = true;
        setIsAnalyzing(true);
        try {
          console.log('🔄 [COMPONENT DEBUG] Loading infrastructure data for case:', activeCase.title, 'ID:', activeCase.id);
          
          // Get real data from the backend
          const infrastructureData = await infrastructureService.getAllInfrastructureData(activeCase.id);
          
          console.log('✅ [COMPONENT DEBUG] Infrastructure data received:', infrastructureData.length, 'items');
          console.log('📊 [COMPONENT DEBUG] Raw infrastructure data:', infrastructureData);
          
          if (infrastructureData.length > 0) {
            const firstItem = infrastructureData[0];
            console.log('🔍 [COMPONENT DEBUG] First item details:');
            console.log('   - hostname:', firstItem.hostname);
            console.log('   - registrar:', firstItem.domainData?.registrar);
            console.log('   - IP:', firstItem.hostingData?.ipAddress);
            console.log('   - ISP:', firstItem.hostingData?.isp);
            console.log('   - Country:', firstItem.hostingData?.country);
          } else {
            console.log('⚠️ [COMPONENT DEBUG] No infrastructure data received!');
          }
          
          setAnalysisData(infrastructureData);
          console.log('💾 [COMPONENT DEBUG] Data set in component state');
        } catch (error) {
          console.error('❌ [COMPONENT DEBUG] Failed to load infrastructure data:', error);
          toast.error('Failed to load infrastructure data from evidence package');
          setAnalysisData([]);
        } finally {
          setIsAnalyzing(false);
          isLoadingRef.current = false;
        }
      } else {
        console.log('⚠️ [COMPONENT DEBUG] No active case provided');
      }
    };

    loadInfrastructureData();
  }, [activeCase]);

  const toggleExpanded = (url: string) => {
    const newExpanded = new Set(expandedUrls);
    if (newExpanded.has(url)) {
      newExpanded.delete(url);
    } else {
      newExpanded.add(url);
    }
    setExpandedUrls(newExpanded);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getRiskColor = (score: number) => {
    if (score >= 80) return "text-red-500 bg-red-500/10 border-red-500/20";
    if (score >= 60) return "text-orange-500 bg-orange-500/10 border-orange-500/20";
    return "text-yellow-500 bg-yellow-500/10 border-yellow-500/20";
  };

  const formatDate = (dateString: string) => {
    // Handle "Unknown" or invalid dates gracefully
    if (!dateString || dateString === 'Unknown' || dateString === 'null') {
      return 'Unknown';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Unknown';
    }
  };

  if (!activeCase) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-4">
          <Server className="w-16 h-16 text-muted-foreground mx-auto" />
          <div>
            <h3>No Active Case</h3>
            <p className="text-muted-foreground">Select a case to view infrastructure analysis</p>
          </div>
        </div>
      </div>
    );
  }

  if (isAnalyzing) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-4">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          <div>
            <h3>Analyzing Infrastructure</h3>
            <p className="text-muted-foreground">Processing {activeCase.urls.length} URLs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2>Infrastructure Analysis</h2>
          <p className="text-muted-foreground">
            Comprehensive metadata analysis for {analysisData.length} URLs
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
          <Button variant="outline" size="sm">
            <Zap className="w-4 h-4 mr-2" />
            Refresh Analysis
          </Button>
        </div>
      </div>

      {/* Analysis Results */}
      <div className="space-y-4">
        {analysisData.map((analysis) => (
          <Card key={analysis.url} className="border-l-4 border-l-primary">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3">
                  <Collapsible>
                    <CollapsibleTrigger 
                      onClick={() => toggleExpanded(analysis.url)}
                      className="flex items-center gap-2 hover:bg-accent/50 p-2 rounded transition-colors"
                    >
                      {expandedUrls.has(analysis.url) ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                      <Globe className="w-5 h-5 text-primary" />
                      <span className="font-mono text-sm">{analysis.hostname}</span>
                    </CollapsibleTrigger>
                  </Collapsible>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className={getRiskColor(analysis.riskScore)} variant="outline">
                    Risk: {analysis.riskScore}/100
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    {analysis.lastUpdated.toLocaleTimeString()}
                  </Badge>
                </div>
              </div>
            </CardHeader>

            <Collapsible open={expandedUrls.has(analysis.url)}>
              <CollapsibleContent>
                <CardContent className="space-y-6">
                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-2 p-4 bg-muted/50 rounded-lg">
                    <Button size="sm" variant="outline" className="text-xs">
                      <Scale className="w-3 h-3 mr-1" />
                      Litigation Hold
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs">
                      <Gavel className="w-3 h-3 mr-1" />
                      Subpoena {analysis.hostingData.isp}
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs">
                      <Ban className="w-3 h-3 mr-1" />
                      Request Takedown
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs">
                      <Shield className="w-3 h-3 mr-1" />
                      Evidence Preservation
                    </Button>
                  </div>

                  {/* Domain & DNS Layer */}
                  <div>
                    <h4 className="flex items-center gap-2 mb-3">
                      <Globe className="w-4 h-4 text-blue-500" />
                      Domain & DNS Layer
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Registrar:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{analysis.domainData.registrar}</span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => copyToClipboard(analysis.domainData.registrar, 'Registrar')}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Registration:</span>
                          <span className="text-sm">{formatDate(analysis.domainData.registrationDate)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Expiration:</span>
                          <span className="text-sm">{formatDate(analysis.domainData.expirationDate)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">WHOIS Privacy:</span>
                          <Badge variant={analysis.domainData.whoisPrivacy ? "destructive" : "secondary"} className="text-xs">
                            {analysis.domainData.whoisPrivacy ? "Protected" : "Public"}
                          </Badge>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm text-muted-foreground">Nameservers:</span>
                          {analysis.domainData.nameservers.map((ns, i) => (
                            <div key={i} className="flex items-center justify-between mt-1">
                              <span className="text-sm font-mono">{ns}</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => copyToClipboard(ns, 'Nameserver')}
                                className="h-6 w-6 p-0"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Abuse Contact:</span>
                          <div className="flex items-center gap-2">
                            <a 
                              href={`mailto:${analysis.domainData.abuseContact}`}
                              className="text-sm text-primary hover:underline"
                            >
                              {analysis.domainData.abuseContact}
                            </a>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => copyToClipboard(analysis.domainData.abuseContact, 'Abuse Contact')}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Info for Registrar */}
                    {analysis.domainData.registrar && (
                      <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <h5 className="font-medium text-sm mb-2">Legal Contact: {analysis.domainData.registrar}</h5>
                        {(() => {
                          const contact = getContactInfo(analysis.domainData.registrar);
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                              <div>Legal Email: <a href={`mailto:${contact.legalEmail}`} className="text-primary">{contact.legalEmail}</a></div>
                              <div>Abuse Email: <a href={`mailto:${contact.abuseEmail}`} className="text-primary">{contact.abuseEmail}</a></div>
                              <div className="md:col-span-2">Subpoena Process: {contact.subpoenaProcess}</div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Hosting Infrastructure */}
                  <div>
                    <h4 className="flex items-center gap-2 mb-3">
                      <Server className="w-4 h-4 text-green-500" />
                      Hosting Infrastructure
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">IP Address:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono">{analysis.hostingData.ipAddress}</span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => copyToClipboard(analysis.hostingData.ipAddress, 'IP Address')}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">ASN:</span>
                          <span className="text-sm font-mono">{analysis.hostingData.asn}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">ISP/Host:</span>
                          <span className="text-sm font-medium">{analysis.hostingData.isp}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Location:</span>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span className="text-sm">{analysis.hostingData.city}, {analysis.hostingData.country}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">CDN:</span>
                          <span className="text-sm">{analysis.hostingData.cdn || "None detected"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">SSL Status:</span>
                          <Badge variant={analysis.hostingData.sslStatus === "Valid" ? "secondary" : "destructive"} className="text-xs">
                            {analysis.hostingData.sslStatus}
                          </Badge>
                        </div>
                        <div>
                          <span className="text-sm text-muted-foreground">Open Ports:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {analysis.hostingData.ports.map(port => (
                              <Badge key={port} variant="outline" className="text-xs">
                                {port}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Info for Hosting Provider */}
                    <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                      <h5 className="font-medium text-sm mb-2">Legal Contact: {analysis.hostingData.isp}</h5>
                      {(() => {
                        const contact = getContactInfo(analysis.hostingData.isp);
                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            <div>Legal Email: <a href={`mailto:${contact.legalEmail}`} className="text-primary">{contact.legalEmail}</a></div>
                            <div>Abuse Email: <a href={`mailto:${contact.abuseEmail}`} className="text-primary">{contact.abuseEmail}</a></div>
                            <div>Phone: {contact.phone}</div>
                            <div>Jurisdiction: {contact.jurisdiction}</div>
                            <div className="md:col-span-2">Takedown Process: {contact.takedownProcess}</div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <Separator />

                  {/* Tracking & Analytics */}
                  <div>
                    <h4 className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-purple-500" />
                      Tracking & Analytics
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {analysis.analytics.googleAnalytics && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Google Analytics:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono">{analysis.analytics.googleAnalytics}</span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => copyToClipboard(analysis.analytics.googleAnalytics!, 'GA ID')}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                      {analysis.analytics.facebookPixel && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Facebook Pixel:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono">{analysis.analytics.facebookPixel}</span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => copyToClipboard(analysis.analytics.facebookPixel!, 'FB Pixel')}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                      {analysis.analytics.other.length > 0 && (
                        <div className="md:col-span-2">
                          <span className="text-sm text-muted-foreground">Other Trackers:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {analysis.analytics.other.map(tracker => (
                              <Badge key={tracker} variant="outline" className="text-xs">
                                {tracker}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {analysis.monetization.tagManagementServices.length > 0 && (
                        <div className="md:col-span-2">
                          <span className="text-sm text-muted-foreground">Tag Management:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {analysis.monetization.tagManagementServices.map(service => (
                              <Badge key={service} variant="outline" className="text-xs bg-blue-500/10">
                                <Zap className="w-3 h-3 mr-1" />
                                {service}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {analysis.monetization.otherServices.length > 0 && (
                        <div className="md:col-span-2">
                          <span className="text-sm text-muted-foreground">Other Services:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {analysis.monetization.otherServices.map(service => (
                              <Badge key={service} variant="outline" className="text-xs bg-gray-500/10">
                                <Server className="w-3 h-3 mr-1" />
                                {service}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Monetization */}
                  <div>
                    <h4 className="flex items-center gap-2 mb-3">
                      <DollarSign className="w-4 h-4 text-yellow-500" />
                      Monetization
                      <Badge variant="outline" className="text-xs">
                        {analysis.monetization.totalExternalServices} services
                      </Badge>
                    </h4>
                    <div className="space-y-3">
                      {analysis.monetization.adNetworks.length > 0 && (
                        <div>
                          <span className="text-sm text-muted-foreground">Advertising Networks:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {analysis.monetization.adNetworks.map(network => (
                              <Badge key={network} variant="outline" className="text-xs bg-red-500/10">
                                <TrendingUp className="w-3 h-3 mr-1" />
                                {network}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {analysis.monetization.paymentProcessors.length > 0 && (
                        <div>
                          <span className="text-sm text-muted-foreground">Payment Processors:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {analysis.monetization.paymentProcessors.map(processor => (
                              <Badge key={processor} variant="outline" className="text-xs bg-yellow-500/10">
                                <CreditCard className="w-3 h-3 mr-1" />
                                {processor}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {analysis.monetization.cryptoAddresses.length > 0 && (
                        <div>
                          <span className="text-sm text-muted-foreground">Crypto Addresses:</span>
                          {analysis.monetization.cryptoAddresses.map((address, i) => (
                            <div key={i} className="flex items-center justify-between mt-1">
                              <span className="text-sm font-mono text-xs">{address}</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => copyToClipboard(address, 'Crypto Address')}
                                className="h-6 w-6 p-0"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {analysis.monetization.totalExternalServices === 0 && (
                        <div className="text-sm text-muted-foreground italic">
                          No monetization services detected
                        </div>
                      )}
                    </div>

                    {/* Contact Info for Payment Processors */}
                    {analysis.monetization.paymentProcessors.map(processor => (
                      <div key={processor} className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                        <h5 className="font-medium text-sm mb-2">Legal Contact: {processor}</h5>
                        {(() => {
                          const contact = getContactInfo(processor);
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                              <div>Legal Email: <a href={`mailto:${contact.legalEmail}`} className="text-primary">{contact.legalEmail}</a></div>
                              <div>Phone: {contact.phone}</div>
                              <div className="md:col-span-2">Litigation Hold: {contact.litigationHold}</div>
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {/* Search Engine Indexing */}
                  <div>
                    <h4 className="flex items-center gap-2 mb-3">
                      <Search className="w-4 h-4 text-orange-500" />
                      Search Engine Indexing
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Google:</span>
                        <Badge variant={analysis.searchIndexing.googleIndexed ? "secondary" : "destructive"} className="text-xs">
                          {analysis.searchIndexing.googleIndexed ? "Indexed" : "Not Indexed"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Bing:</span>
                        <Badge variant={analysis.searchIndexing.bingIndexed ? "secondary" : "destructive"} className="text-xs">
                          {analysis.searchIndexing.bingIndexed ? "Indexed" : "Not Indexed"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Yandex:</span>
                        <Badge variant={analysis.searchIndexing.yandexIndexed ? "secondary" : "destructive"} className="text-xs">
                          {analysis.searchIndexing.yandexIndexed ? "Indexed" : "Not Indexed"}
                        </Badge>
                      </div>
                    </div>
                    
                    {(analysis.searchIndexing.sitemap || analysis.searchIndexing.robotsTxt) && (
                      <div className="mt-3 space-y-2">
                        {analysis.searchIndexing.sitemap && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Sitemap:</span>
                            <a 
                              href={analysis.searchIndexing.sitemap} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1"
                            >
                              View <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                        {analysis.searchIndexing.robotsTxt && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Robots.txt:</span>
                            <a 
                              href={analysis.searchIndexing.robotsTxt} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1"
                            >
                              View <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Search Engine Contacts */}
                    <div className="mt-4 space-y-2">
                      <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                        <h5 className="font-medium text-sm mb-2">Search Engine Removal Contacts</h5>
                        <div className="space-y-1 text-xs">
                          <div>Google: <a href="https://www.google.com/webmasters/tools/legal-removal-request" className="text-primary">Legal Removal Request</a></div>
                          <div>Bing: <a href="https://www.bing.com/webmaster/tools/contentremoval" className="text-primary">Content Removal Tool</a></div>
                          <div>Yandex: <a href="mailto:abuse@yandex.com" className="text-primary">abuse@yandex.com</a></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))}
      </div>

      {/* Summary Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Analysis Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{analysisData.length}</div>
              <div className="text-sm text-muted-foreground">URLs Analyzed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">
                {analysisData.filter(a => a.hostingData.country === "United States").length}
              </div>
              <div className="text-sm text-muted-foreground">US Hosted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-500">
                {analysisData.filter(a => a.monetization.paymentProcessors.length > 0).length}
              </div>
              <div className="text-sm text-muted-foreground">With Payment Processing</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-500">
                {analysisData.length > 0 ? Math.round(analysisData.reduce((acc, a) => acc + a.riskScore, 0) / analysisData.length) : 0}
              </div>
              <div className="text-sm text-muted-foreground">Avg Risk Score</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}